import PouchDB from "pouchdb-browser";
import store from "../config/store";

export class PouchDbRepository {
  dbLocal;
  dbRemoto;
  urlDatabase = `${store.baseUrl}db-sinc`;
  tentativaConeccao = 0;
  databaseName;
  batchSize = 5000;
  batchesLimit = 40;
  checkpoint = "source";
  maximaTentativaConeccao = 5;
  tempoReconeccao = 10 * 60 * 1000;
  timeoutId = null;
  syncHandler = null;
  timeoutPouchDB = 60000;

  constructor(database) {
    this.databaseName = database;
    this.dbLocal = new PouchDB(database);
    this.dbRemoto = new PouchDB(`${this.urlDatabase}/${database}`, {
      fetch: (url, opts) => {
        const token = localStorage.getItem("token");
        if (token) {
          opts.headers.set("Authorization", `Bearer ${token}`);
        }
        return PouchDB.fetch(url, opts);
      },
    });
  }

  async startSync() {
    if (this.syncHandler && this.syncHandler.cancel) {
      try {
        this.syncHandler.cancel();
      } catch (e) {
        console.debug(
          "SincRepository: erro ao cancelar sincronização anterior",
          e,
        );
      }
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
      console.debug(
        "SincRepository: cancelado retry agendado antes de iniciar sincronização",
      );
    }
    try {
      this.onceSync();
    } catch (err) {
      console.error("SincRepository: falha ao inicializar o banco remoto", err);
      this.handleSyncError();
    }
  }

  async startLiveSync() {
    if (this.syncHandler && this.syncHandler.cancel) {
      try {
        this.syncHandler.cancel();
      } catch (e) {
        console.debug(
          "SincRepository: erro ao cancelar sincronização anterior",
          e,
        );
      }
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
      console.debug(
        "SincRepository: cancelado retry agendado antes de iniciar sincronização",
      );
    }
    try {
      this.liveSinc();
    } catch (err) {
      console.error("SincRepository: falha ao inicializar o banco remoto", err);
      this.handleSyncError();
    }
  }

  liveSinc() {
    this.syncHandler = this.dbLocal
      .sync(this.dbRemoto, {
        live: true,
        retry: true,
        batch_size: this.batchSize,
        batches_limit: this.batchesLimit,
        checkpoint: this.checkpoint,
        timeout: this.timeoutPouchDB,
      })
      .on("change", (info) => {
        this.tentativaConeccao = 0;
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
      })
      .on("paused", (err) => console.debug("sincronização pausada", err))
      .on("active", () => {
        console.debug("sincronização ativa");
        this.tentativaConeccao = 0;
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
      })
      .on("denied", (err) => console.error("sync denied", err))
      .on("complete", (info) => {
        this.tentativaConeccao = 0;
      })
      .on("error", (err) => {
        console.error("erro de sincronização", err);
        this.handleSyncError();
      });
  }

  async onceSync() {
    return new Promise((resolve, reject) => {
      this.dbLocal
        .sync(this.dbRemoto, {
          batch_size: this.batchSize,
          batches_limit: this.batchesLimit,
          checkpoint: this.checkpoint,
          timeout: this.timeoutPouchDB,
        })
        .on("complete", (info) => {
          this.tentativaConeccao = 0;
          resolve(info);
        })
        .on("error", (err) => {
          console.error("Erro na sincronização bidirecional", err);
          reject(err);
        });
    });
  }

  handleSyncError() {
    if (this.timeoutId) return;
    if (this.tentativaConeccao < this.maximaTentativaConeccao) {
      this.tentativaConeccao += 1;
      console.warn(
        `Erro de sincronização do SincRepository,(tentativa ${this.tentativaConeccao})`,
      );
      try {
        this.startSync();
      } catch (e) {
        console.error("SincRepository: falha ao reiniciar sincronização", e);
        this.handleSyncError();
      }
    } else {
      console.warn(
        `Erro de sincronização do SincRepository, número máximo de tentativas (${this.maximaTentativaConeccao}) atingido. Irá tentar novamente em ${this.tempoReconeccao}ms (retentativa longa)`,
      );
      this.tentativaConeccao = 0;
      this.timeoutId = setTimeout(() => {
        this.timeoutId = null;
        try {
          this.startSync();
        } catch (e) {
          console.error("SincRepository: falha ao reiniciar sincronização", e);
          this.handleSyncError();
        }
      }, this.tempoReconeccao);
    }
  }

  async getAll(filtros = {}, opcoes = {}) {
    const {
      modo = "and",
      exatos = [],
      where = {},
      orderBy = null,
      ordem = "asc",
      limite = null,
      ignorarExcluidos = true,
    } = opcoes;
    const res = await this.dbLocal.allDocs({ include_docs: true });
    let documentos = res.rows.map((row) => ({
      ...row.doc,
      id: parseInt(row.doc._id),
    }));
    if (ignorarExcluidos) {
      documentos = documentos.filter(
        (doc) => doc.deletedAt === undefined || doc.deletedAt === null,
      );
    }
    if (Object.keys(where).length) {
      documentos = documentos.filter((doc) => this.atendeWhere(doc, where));
    }
    const chavesFiltro = Object.keys(filtros).filter(
      (chave) =>
        filtros[chave] !== null &&
        filtros[chave] !== undefined &&
        filtros[chave] !== "",
    );
    if (chavesFiltro.length) {
      documentos = documentos.filter((doc) => {
        const comparar = (chave) =>
          this.comparaValor(doc[chave], filtros[chave], exatos.includes(chave));
        return modo === "or"
          ? chavesFiltro.some(comparar)
          : chavesFiltro.every(comparar);
      });
    }
    if (orderBy) {
      documentos.sort((docA, docB) => {
        const comparacao = String(docA[orderBy] ?? "").localeCompare(
          String(docB[orderBy] ?? ""),
          "pt-BR",
          { numeric: true, sensitivity: "base" },
        );
        return ordem === "desc" ? -comparacao : comparacao;
      });
    }
    return limite ? documentos.slice(0, limite) : documentos;
  }

  atendeWhere(doc, where) {
    return Object.keys(where).every((chave) => doc[chave] === where[chave]);
  }

  comparaValor(valorDocumento, valorFiltro, exato) {
    if (valorDocumento === undefined || valorDocumento === null) return false;
    if (Array.isArray(valorFiltro)) {
      return valorFiltro.some((item) =>
        this.comparaValor(valorDocumento, item, exato),
      );
    }
    if (typeof valorDocumento === "boolean") {
      return valorDocumento === Boolean(valorFiltro);
    }
    const alvo = String(valorFiltro).trim().toLowerCase();
    const valor = String(valorDocumento).trim().toLowerCase();
    return exato ? valor === alvo : valor.includes(alvo);
  }

  async insert(dados) {
    try {
      const resultado = await this.dbLocal.post(dados);
      return { ...resultado, success: true };
    } catch (err) {
      console.error("Erro ao inserir documento no banco local", err);
      return { success: false, error: err };
    }
  }

  async getById(id) {
    try {
      const doc = await this.dbLocal.get(String(id));
      return { ...doc, success: true };
    } catch (err) {
      console.error("Erro ao buscar documento no banco local", err);
      return { success: false, error: err };
    }
  }

  async update(dados) {
    try {
      const resultado = await this.dbLocal.put(dados);
      return { ...resultado, success: true };
    } catch (err) {
      console.error("Erro ao atualizar documento no banco local", err);
      return { success: false, error: err };
    }
  }

  stopSync(limparDbLocal) {
    if (this.syncHandler && this.syncHandler.cancel) {
      try {
        this.syncHandler.cancel();
        this.syncHandler = null;
      } catch (e) {
        console.error("SincRepository: erro ao cancelar sincronização", e);
      }
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (limparDbLocal) {
      this.dbLocal = null;
    }
  }

  async deleteDatabase() {
    this.stopSync(false);
    try {
      await this.dbLocal.destroy();
      this.dbLocal = null;
    } catch (err) {
      console.error("SincRepository: erro ao deletar o banco local", err);
      throw err;
    }
  }
}
